import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Clapperboard, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { toast } from '@/components/ui/toast';

interface Tier {
  label: string;
  hint: string;
  values: number[];
}

const TIERS: Tier[] = [
  { label: '轻量', hint: '~1-3 小时', values: [24, 30, 36] },
  { label: '中等', hint: '~2-6 小时', values: [48, 60, 72] },
  { label: '长篇', hint: '~3-8 小时', values: [84, 99] },
];

const PRESET_VALUES = new Set(TIERS.flatMap((t) => t.values));

interface Props {
  episodeId: string;
  value: number;
  hasSelected: boolean;
  onSaved: (n: number, duration: number) => void;
  locked?: boolean;
  episodeDuration?: number;
  onDurationChange?: (d: number) => void;
}

export function EpisodeCountPicker({
  episodeId,
  value,
  hasSelected,
  onSaved,
  locked = false,
  episodeDuration,
  onDurationChange,
}: Props) {
  const [localDuration, setLocalDuration] = useState(episodeDuration ?? 3);
  const mut = useMutation({
    mutationFn: async (n: number) =>
      api
        .patch(`writer/episodes/${episodeId}/episode-count`, {
          json: { episodeCount: n, episodeDuration: localDuration },
        })
        .json<{ episodeCount: number; updatedAt: string }>(),
    onSuccess: (data) => {
      onSaved(data.episodeCount, localDuration);
      toast.success('已更新剧情集数', `按 ${data.episodeCount} 集展开`);
    },
    onError: (err: Error) => toast.error('集数更新失败', err.message),
  });

  const pendingTarget = mut.variables;

  return (
    <div className="space-y-2 rounded-lg border border-outline-variant/30 bg-surface-variant/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-outline">
          <Clapperboard className="h-3.5 w-3.5" />
          剧情集数
        </div>
        <span className="text-xs text-neutral-500">
          用于「剧情脚本」按集数分卷
        </span>
      </div>

      {!hasSelected && !locked && (
        <div className="flex items-center gap-1.5 rounded-md bg-warning-soft/50 px-2 py-1 text-[11px] text-warning">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>请先选择剧情集数,才能生成「剧情脚本」</span>
        </div>
      )}

      <div className="space-y-1.5">
        {hasSelected && !PRESET_VALUES.has(value) && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-md bg-surface px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-outline">
              自定义
              <span className="text-[10px] normal-case text-neutral-400">来自原文</span>
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-500 px-3 py-1 text-xs font-semibold text-white shadow-sm">
              {value} 集
            </span>
          </div>
        )}
        {TIERS.map((tier) => (
          <div key={tier.label} className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-md bg-surface px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-outline">
              {tier.label}
              <span className="text-[10px] normal-case text-neutral-400">{tier.hint}</span>
            </span>
            {tier.values.map((n) => {
              const active = hasSelected && n === value;
              const isPending = mut.isPending && pendingTarget === n;
              return (
                <button
                  key={n}
                  type="button"
                  disabled={locked || mut.isPending || (active && hasSelected)}
                  onClick={() => mut.mutate(n)}
                  className={
                    active
                      ? 'inline-flex items-center gap-1 rounded-full bg-brand-500 px-3 py-1 text-xs font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-80'
                      : 'inline-flex items-center gap-1 rounded-full border border-outline-variant/40 bg-surface px-3 py-1 text-xs text-neutral-700 transition hover:border-brand-400 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-60'
                  }
                >
                  {isPending ? '保存中…' : `${n} 集`}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="mt-3">
        <div className="mb-1.5 text-xs text-neutral-500">每集剧情长度</div>
        <div className="flex gap-2">
          {([3, 5, 10] as const).map((d) => (
            <button
              key={d}
              type="button"
              disabled={locked}
              onClick={() => { setLocalDuration(d); onDurationChange?.(d); }}
              className={cn(
                'rounded-full border px-3 py-0.5 text-xs transition',
                localDuration === d
                  ? 'border-brand-500 bg-brand-500/10 text-brand-600 font-semibold'
                  : 'border-outline-variant text-neutral-600 hover:border-brand-400 disabled:cursor-not-allowed disabled:opacity-60',
              )}
            >
              {d} 分钟
            </button>
          ))}
        </div>
        <p className="mt-1 text-[11px] text-neutral-400">
          {localDuration === 3 ? '每集约 7-9 个场景，脚本 ≥800 字'
            : localDuration === 5 ? '每集约 10-14 个场景，脚本 ≥1500 字'
            : '每集约 18-24 个场景，脚本 ≥3000 字'}
        </p>
      </div>

      {locked && (
        <p className="text-[11px] text-neutral-500">
          项目已初始化 · 集数已锁定,不可再修改
        </p>
      )}
    </div>
  );
}
